// Copyright 2026, University of Maryland, All Rights Reserved
import { type FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Bot, ChevronRight, LayoutDashboard, LayoutGrid, Plus, Workflow } from 'lucide-react'

import { useServices } from '@/app-shell/services'
import { useAuth } from '@/subsystems/workspaces'
import { useAgentTemplates } from '@/subsystems/agents'

const AgentLibraryPage: FC = () => {
  const navigate = useNavigate()
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const services = useServices()
  const orgId = selectedOrganizationId ?? ''
  const wsId = selectedWorkspaceId ?? ''

  const { data: templates = [], isLoading } = useAgentTemplates(services.getClient(), orgId, wsId)

  if (!orgId || !wsId) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
        Select a workspace to view the agent library.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="container mx-auto space-y-7 px-6 py-8">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="flex items-baseline gap-4">
            <h1 className="text-3xl font-semibold tracking-tight">Agent Library</h1>
            <span className="text-sm font-medium text-slate-500">
              {templates.length} {templates.length === 1 ? 'agent' : 'agents'}
            </span>
          </div>
        <p className="text-sm text-muted-foreground mt-1">
          Agent types available for this application. Manage templates by promoting
          existing workspace agents from their Settings tab.
        </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/agents/new')}
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          New Agent
        </button>
      </div>

      {templates.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No agent types have been configured for this application yet.
        </p>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {templates.map(template => {
            const rendererCount = Object.keys(template.renderers ?? {}).length
            return (
              <button
                key={template.agentId}
                type="button"
                onClick={() => navigate(`/agent-library/${template.agentId}`)}
                className="group flex min-h-64 flex-col overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <div className="flex items-start gap-3 px-5 pb-3 pt-5">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <Bot className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{template.label}</div>
                    <div className="text-xs font-mono text-muted-foreground truncate">
                      {template.agentId}
                    </div>
                  </div>
                  <ChevronRight className="mt-1 h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5" />
                </div>
                {template.description && (
                  <p
                    className="line-clamp-4 px-5 text-sm leading-6 text-muted-foreground"
                    title={template.description}
                  >
                    {template.description}
                  </p>
                )}
                <div className="mt-auto flex flex-wrap gap-2 border-t bg-slate-50/70 px-5 py-3 text-xs text-muted-foreground">
                  {template.pipelineId && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                      <Workflow className="h-3 w-3" />
                      Pipeline
                    </span>
                  )}
                  {rendererCount > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                      <LayoutGrid className="h-3 w-3" />
                      {rendererCount} {rendererCount === 1 ? 'renderer' : 'renderers'}
                    </span>
                  )}
                  {template.dashboardId && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                      <LayoutDashboard className="h-3 w-3" />
                      Dashboard
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default AgentLibraryPage
